/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-07 13:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { Modal, TextContainer } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { formatNumberInThousand } from 'src/helpers';
import { Field, reduxForm, reset, getFormValues } from 'redux-form';
import { prettyText } from 'src/helpers';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import * as supportTicketActions from 'actions/support-ticket-actions';
import { isRequired, isEmail, isExceedLength } from 'src/helpers/form-validation-helper';

const isRequiredValue = isRequired();
const isEmailValue = isEmail();
const isExceedLengthValue = isExceedLength(10000);
type Props = {
  active: boolean,
  sending: boolean,
  handleCancel: () => void,
  handleConfirm: () => void
};

type ComposedProps = Props & WithI18nProps;
// DONE
class SendRequestModal extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    const { products: prevProducts = -1 } = this.props;
    const { products = -1, i18n } = nextProps;
    if (prevProducts !== products) {
      const exampleMessage = i18n.translate(
        'AdminHTML.SubscriptionV4.SendRequestModal.exampleRequest',
        {
          numberProducts: formatNumberInThousand(products)
        }
      );
      this.setState({ message: exampleMessage });
    }
  }

  render() {
    const {
      active,
      sending = false,
      i18n,
      formValues = {},
      handleSubmit,
      products = 50000
    } = this.props;
    const { email = '', content = '' } = formValues;

    return (
      <Modal
        open={active}
        onClose={this.handleCancel}
        title={i18n.translate('AdminHTML.SubscriptionV4.SendRequestModal.title')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.sendRequest'),
          loading: sending,
          disabled: _.isEmpty(email) || _.isEmpty(content),
          onAction: handleSubmit(this.handleSendRequest.bind(this))
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleCancel
          }
        ]}>
        <Modal.Section>
          <TextContainer>
            <p>{i18n.translate('AdminHTML.SubscriptionV4.SendRequestModal.content')}</p>
            <Field
              name="email"
              label={i18n.translate('AdminHTML.SubscriptionV4.SendRequestModal.textField_Label')}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.SendRequestModal.textField_Placeholder'
              )}
              component={RenderTextField}
              validate={[isRequiredValue, isEmailValue, isExceedLengthValue]}
            />
            <Field
              name="content"
              label={i18n.translate('AdminHTML.SubscriptionV4.SendRequestModal.textField_Label01')}
              component={RenderTextField}
              anotherProps={{ multiline: 2, autoComplete: 'off' }}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.SendRequestModal.exampleRequest',
                {
                  numberProducts: formatNumberInThousand(products)
                }
              )}
              validate={[isRequiredValue, isExceedLengthValue]}
            />
          </TextContainer>
        </Modal.Section>
      </Modal>
    );
  }

  // Close modal
  handleCancel = () => {
    this.props.handleCancel();
  };

  handleSendRequest = async data => {
    const { sendSupportTicket } = this.props;

    const prettyData = {
      email: prettyText(data.email),
      content: prettyText(data.content),
      selection: {}
    };
    const result = await sendSupportTicket(prettyData);

    if (result.type === 'SEND_SUPPORT_TICKET_SUCCESS') {
      this.props.handleCancel();
    }
  };
}

const mapStateToProps = (state, props) => {
  const { products } = props;
  const { supportTicket } = state;
  const formValues = getFormValues('SendRequestModal')(state);
  return {
    initialValues: {
      email: '',
      content: ''
    },
    products,
    formValues,
    sendRequestLoading: _.get(supportTicket, 'sendSupportTicketLoading', false),
    sendRequestSuccess: _.get(supportTicket, 'sendSupportTicketSuccess', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendSupportTicket: data =>
    dispatch(supportTicketActions.sendSupportTicket(data, [], ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(supportTicketActions.resetSendSupportTicket()),
  resetForm: () => dispatch(reset('SendRequestModal'))
});

const SendRequestModalForm = reduxForm({
  form: 'SendRequestModal', // a unique identifier for this form
  enableReinitialize: true
})(SendRequestModal);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SendRequestModalForm));
